import React from "react";
import { Card, Box, Typography, Grid, CardActionArea, Button } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";

const MyJobOffersStats = ({ jobStatus }) => {
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const navigate = useNavigate();

  const JobOffersClosingSoon = jobStatus?.filter((job) => {
    const endOfMandate = new Date(job.end_of_mandate);
    const today = new Date();
    const daysDiff = Math.ceil((endOfMandate - today) / (1000 * 3600 * 24));
    return daysDiff > 0 && daysDiff <= 7 && job.offer_status === "online";
  });

  const useStyles = makeStyles(() => ({
    mainCardContainer: {
      boxShadow: "none",
      borderRadius: "12px",
      padding: "20px",
      flex: 1,
      height: "100%",
      minHeight: 0,
      border: `1px solid ${theme.palette.background.neutral}`,
    },
    contentCardContainer: {
      padding: "7px 12px",
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      flexDirection: "column",
      height: "100%",
      borderRadius: "12px", 
      boxShadow: "none",
      flex: 1,
      minHeight: 0,
      border: `1px solid ${theme.palette.background.neutral}`,
      gap: "10px",
      width: isDesktop ? "auto" : "100%",
    },
    cardActionArea: {
      "&:hover .MuiCardActionArea-focusHighlight": {
        opacity: 0,
      },
    },
    statusCountBox: {
      backgroundColor: theme.palette.secondary.box,
      borderRadius: "8px",
      padding: "4px",
    },
    boardButton: {
      borderRadius: "999px",
      height: "30px",
      color: `${theme.palette.text.main} !important`, 
      border: `1px solid ${theme.palette.background.neutral} !important`,
      minWidth: "auto",
      padding: "6px 24px",
      fontSize: isDesktop ? "inherit" : "0.7rem",
      backgroundColor: "#FFF",
      textTransform: "none",
      fontWeight: "600 !important",
      fontFamily: "Segoe UI Semibold, sans-serif",
      width: isDesktop ? "auto" : "100%",
      mt: "auto",
      "&:hover": {
        backgroundColor: theme.palette.secondary?.hover,
      },
    },
  }));

  const classes = useStyles();

  return(
    <Box sx={{ flex: 1 }}>
      <Card className={classes.mainCardContainer}>
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", overflowX: "auto"}}>
          <Typography variant={isDesktop ? "h5" : "h3"} sx={{fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "20px !important"}}>
            <b><FormattedMessage id="recruiter.overview.my_job_offers" /></b>
          </Typography>
        </Box>

        <Grid container spacing={1} xs={12} sx={{mt: "20px", rowGap: isDesktop ? 0.5 : 0 }}>
          <Grid item xs={12}>
            <CardActionArea className={classes.cardActionArea} onClick={() => navigate(`/${i18nLocale}/recruiter?tab=jobs&jobs_filter=online`)}>
              <Card className={classes.contentCardContainer}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
                  <Typography variant={isDesktop ? "h6" : "h3"} sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="job_offer.online" />
                  </Typography>
                  <Box className={classes.statusCountBox}>
                    <Typography variant={isDesktop ? "h5" : "h3"} sx={{fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif"}}>
                      {jobStatus?.filter(job => job.offer_status === "online").length || 0}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body3">
                    <FormattedMessage id="recruiter.overview.online_jobs_description" />
                  </Typography>
                </Box>
              </Card>
            </CardActionArea>
          </Grid>
          <Grid item xs={12}>
            <CardActionArea className={classes.cardActionArea} onClick={() => navigate(`/${i18nLocale}/recruiter?tab=jobs&jobs_filter=draft`)}>
              <Card className={classes.contentCardContainer}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
                  <Typography variant={isDesktop ? "h6" : "h3"} sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="job_offer.draft" />
                  </Typography>
                  <Box className={classes.statusCountBox}>
                    <Typography variant={isDesktop ? "h5" : "h3"} sx={{fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif"}}>
                      {jobStatus?.filter(job => job.offer_status === "draft").length || 0}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body3">
                    <FormattedMessage id="recruiter.overview.draft_jobs_description" />
                  </Typography>
                </Box>
              </Card>
            </CardActionArea>
          </Grid>
          <Grid item xs={12}>
            <CardActionArea className={classes.cardActionArea} onClick={() => navigate(`/${i18nLocale}/recruiter?tab=jobs&jobs_filter=online`)}>
              <Card className={classes.contentCardContainer}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
                  <Typography variant={isDesktop ? "h6" : "h3"} sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="recruiter.overview.closing_soon" />
                  </Typography>
                  <Box className={classes.statusCountBox}>
                    <Typography variant={isDesktop ? "h5" : "h3"} sx={{fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                      {JobOffersClosingSoon?.length}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body3">
                    <FormattedMessage id="recruiter.overview.closing_soon_jobs_description" />
                  </Typography>
                </Box>
              </Card>
            </CardActionArea>
          </Grid>
          <Grid item xs={12}>
            <CardActionArea className={classes.cardActionArea} onClick={() => navigate(`/${i18nLocale}/recruiter?tab=jobs&jobs_filter=closed`)}>
              <Card className={classes.contentCardContainer}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
                  <Typography variant={isDesktop ? "h6" : "h3"} sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
                    <FormattedMessage id="job_offer.closed" />
                  </Typography>
                  <Box className={classes.statusCountBox}>
                    <Typography variant={isDesktop ? "h5" : "h3"} sx={{fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif"}}>
                      {jobStatus?.filter(job => job.offer_status === "closed")?.length || 0}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body3">
                    <FormattedMessage id="recruiter.overview.closed_jobs_description" />
                  </Typography>
                </Box>
              </Card>
            </CardActionArea>
          </Grid>
        </Grid>
        <Box sx={{mt: "20px"}}>
          <Button variant="outlined" onClick={() => navigate(`/${i18nLocale}/recruiter?tab=jobs`)} className={classes.boardButton}>
            <FormattedMessage id="recruiter.overview.all_job_offers" />
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default MyJobOffersStats;